import { Select, Text as MantineText } from '@mantine/core';
import { FC, useContext, useEffect, useMemo, useRef } from 'react';
import { useJwtClaims } from '../../Service/Context/AuthContext';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import { useMailOutboundConfigs } from '../../Service/hooks/useMailOutboundConfigs';
import { MailOutBoundConfigDTO } from '../../Types/MailT';

const BOOKITUP_MAIL_SERVER_VALUE = 'BOOKITUP_MAIL_SERVER';

const getConfigLabel = (config: MailOutBoundConfigDTO) => {
  const account = config.description || config.email;
  const email = config.description && config.email ? ` (${config.email})` : '';
  const defaultLabel = config.defaultAccount ? ' - Standard' : '';

  return `${account}${email}${defaultLabel}`;
};

const SenderConfigSelect: FC<{
  value?: number | null;
  onChange: (configId: number | null) => unknown;
}> = ({ value, onChange }) => {
  const { profileId, role, userId } = useJwtClaims();
  const { bookitupTeam } = useContext(ProfileContext);
  const { ensureSharesLoaded, loading: loadingProfileShares, shares, sharesLoaded } = bookitupTeam;
  const { loaded, loading, mailOutboundConfigs } = useMailOutboundConfigs();
  const prefilledDefaultConfigId = useRef<number | null>(null);
  const bookitupMailServerEmail = `profile-${profileId}@bookitup.de`;
  const hasOutboundConfigs = mailOutboundConfigs.length > 0;
  const isTenant = role === 'TENANT';

  useEffect(() => {
    if (isTenant) {
      ensureSharesLoaded().catch(() => undefined);
    }
  }, [ensureSharesLoaded, isTenant]);

  const profileShare = useMemo(
    () => (isTenant && userId ? shares.find((share) => share.applicationUserId === userId) : undefined),
    [isTenant, shares, userId],
  );

  const visibleMailOutboundConfigs = useMemo(() => {
    if (!isTenant) {
      return mailOutboundConfigs;
    }

    if (!userId || !sharesLoaded) {
      return [];
    }

    const availableMailAccounts = profileShare?.availableMailAccounts || [];

    return mailOutboundConfigs.filter((config) => availableMailAccounts.includes(config.id));
  }, [isTenant, mailOutboundConfigs, profileShare?.availableMailAccounts, sharesLoaded, userId]);

  const selectableMailOutboundConfigs = useMemo(
    () => visibleMailOutboundConfigs.filter((config) => config.connectionVerified),
    [visibleMailOutboundConfigs],
  );

  const defaultConfigId = useMemo(() => {
    const selectableMailAccountIds = selectableMailOutboundConfigs.map((config) => config.id);

    if (
      isTenant &&
      profileShare?.defaultAccount !== null &&
      profileShare?.defaultAccount !== undefined &&
      selectableMailAccountIds.includes(profileShare.defaultAccount)
    ) {
      return profileShare.defaultAccount;
    }

    return selectableMailOutboundConfigs.find((config) => config.defaultAccount)?.id;
  }, [isTenant, profileShare?.defaultAccount, selectableMailOutboundConfigs]);

  useEffect(() => {
    if (value === null || value === undefined) {
      if (defaultConfigId && prefilledDefaultConfigId.current !== defaultConfigId) {
        prefilledDefaultConfigId.current = defaultConfigId;
        onChange(defaultConfigId);
      }
    }
  }, [defaultConfigId, onChange, value]);

  useEffect(() => {
    if (!isTenant || !loaded || !sharesLoaded || value === null || value === undefined) {
      return;
    }

    if (!selectableMailOutboundConfigs.some((config) => config.id === value)) {
      onChange(null);
    }
  }, [isTenant, loaded, onChange, selectableMailOutboundConfigs, sharesLoaded, value]);

  const data = useMemo(() => {
    if (loaded && !hasOutboundConfigs) {
      return [
        {
          value: BOOKITUP_MAIL_SERVER_VALUE,
          label: `${bookitupMailServerEmail} - bookitup E-Mail-Server`,
        },
      ];
    }

    return selectableMailOutboundConfigs.map((config) => ({
      value: config.id.toString(),
      label: getConfigLabel(config),
    }));
  }, [bookitupMailServerEmail, hasOutboundConfigs, loaded, selectableMailOutboundConfigs]);

  const getSelectValue = () => {
    if (value !== null && value !== undefined) {
      if (isTenant && loaded && sharesLoaded && !selectableMailOutboundConfigs.some((config) => config.id === value)) {
        return null;
      }

      return value.toString();
    }
    if (loaded && !hasOutboundConfigs) {
      return BOOKITUP_MAIL_SERVER_VALUE;
    }
    if (defaultConfigId && prefilledDefaultConfigId.current !== defaultConfigId) {
      return defaultConfigId.toString();
    }
    return null;
  };

  return (
    <Select
      clearable
      searchable
      variant="unstyled"
      icon={
        <MantineText size="xs" weight="bolder">
          Absender
        </MantineText>
      }
      iconWidth={70}
      data={data}
      value={getSelectValue()}
      onChange={(configId) => onChange(configId && configId !== BOOKITUP_MAIL_SERVER_VALUE ? Number(configId) : null)}
      disabled={loading || (isTenant && loadingProfileShares && !sharesLoaded)}
      nothingFound="Keine Absender gefunden"
      rightSectionWidth={25}
      sx={(theme) => ({ borderBottom: `1px solid ${theme.colors.gray[4]}` })}
      styles={{ icon: { justifyContent: 'flex-start' }, rightSection: { pointerEvents: 'none' } }}
    />
  );
};

export default SenderConfigSelect;
